import { useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Iconify from '../../components/Iconify';
import { Container, IconButton, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateUserPassword } from '../../store/users/api';
import { toast } from 'react-toastify';
import { omit } from 'lodash';
import PasswordSchema from '../../validation/passwordSchema';

const PasswordChange: React.FC = () => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const userProfile = useAppSelector((state) => state.auth.user);
  const {
    handleSubmit,
    values,
    handleChange,
    isSubmitting,
    errors,
    touched,
    resetForm,
  } = useFormik({
    initialValues: {
      id: userProfile.id,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: PasswordSchema,
    onSubmit: async (values) => {
      try {
        const reqBody = omit(values, ['confirmPassword']);
        const response = await dispatch(updateUserPassword(reqBody)).unwrap();
        resetForm();
        toast.success(response.message);
      } catch (error: any) {}
    },
  });

  const handleShowPassword = (key: string) => {
    setShowPassword((show: any) => ({ ...show, [key]: !show[key] }));
  };

  return (
    <Container maxWidth='xl'>
      <Grid
        item
        xs={12}
        md={6}
      >
        <Card sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                md={12}
              >
                <TextField
                  id='oldPassword'
                  name='oldPassword'
                  label={'Old Password'}
                  fullWidth
                  autoComplete='given-firstName'
                  onChange={handleChange}
                  type={showPassword.oldPassword ? 'text' : 'password'}
                  value={values.oldPassword}
                  error={Boolean(touched.oldPassword && errors.oldPassword)}
                  helperText={
                    Boolean(errors.oldPassword && touched.oldPassword) &&
                    errors.oldPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => handleShowPassword('oldPassword')}
                          edge='end'
                        >
                          <Iconify
                            icon={
                              showPassword.oldPassword
                                ? 'eva:eye-fill'
                                : 'eva:eye-off-fill'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
              >
                <TextField
                  id='newPassword'
                  name='newPassword'
                  label={'New Password'}
                  fullWidth
                  onChange={handleChange}
                  type={showPassword.newPassword ? 'text' : 'password'}
                  value={values.newPassword}
                  error={Boolean(touched.newPassword && errors.newPassword)}
                  helperText={
                    Boolean(errors.newPassword && touched.newPassword) &&
                    errors.newPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => handleShowPassword('newPassword')}
                          edge='end'
                        >
                          <Iconify
                            icon={
                              showPassword.newPassword
                                ? 'eva:eye-fill'
                                : 'eva:eye-off-fill'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
              >
                <TextField
                  id='confirmPassword'
                  name='confirmPassword'
                  label={'Confirm Password'}
                  fullWidth
                  onChange={handleChange}
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  value={values.confirmPassword}
                  error={Boolean(
                    touched.confirmPassword && errors.confirmPassword
                  )}
                  helperText={
                    Boolean(
                      errors.confirmPassword && touched.confirmPassword
                    ) && errors.confirmPassword
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => handleShowPassword('confirmPassword')}
                          edge='end'
                        >
                          <Iconify
                            icon={
                              showPassword.confirmPassword
                                ? 'eva:eye-fill'
                                : 'eva:eye-off-fill'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Stack
              alignItems='flex-end'
              sx={{ mt: 3 }}
            >
              <LoadingButton
                type='submit'
                variant='contained'
                loading={isSubmitting}
              >
                Change Password
              </LoadingButton>
            </Stack>
          </form>
        </Card>
      </Grid>
    </Container>
  );
};

export default PasswordChange;
